import { useRef, useEffect, useState } from 'react';
import { getActiveState, parseJSON } from '../helpers/titreHelper';
import { useGetParams } from './useGetParams';
import { useInterval } from './useInterval';

const loadImages = async (urls) => {
  for (let i = 0; i < urls.length; i += 6) {
    const batch = urls.slice(i, i + 6);

    await Promise.all(
      batch.map((url) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            resolve();
          };
          img.onerror = resolve;
          img.src = url;
        });
      }),
    );
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
};

export const useBroadcast = () => {
  const { user } = useGetParams();

  const [currentState, setCurrentState] = useState(null);
  const [isVisible, setIsVisible] = useState(null);

  const [currentMessage, setCurrentMessage] = useState(null);

  const lastMessageIdRef = useRef(0);
  const messagesRef = useRef([]);

  const protocol = window?.location.protocol || 'https:';

  // id предыдущего запущенного интерактива
  const prevIdBroadcast = useRef('');
  // id текущего запущенного интерактива
  const iactiveIdRef = useRef('');

  const getBroadcastInfo = async () => {
    try {
      if (!user) {
        return;
      }

      const response = await fetch(
        `${protocol}//api.stream.iactive.pro/titreInfo?user=${user}&from=${lastMessageIdRef.current}&type=1`,
        { cache: 'no-store' },
      );
      const data = await response.json();

      if (!data) return;

      let { messagesList, deletedMessagesIdList, visibility, iactiveId } = data;

      // очистка при остановке\смене интерактива, при остановке iactiveId = ""
      if (iactiveIdRef.current !== iactiveId) {
        if (
          iactiveId &&
          iactiveId !== prevIdBroadcast.current // если === , значит prevIdBroadcast был остановлен и теперь запущен, и не нужно обнулять
        ) {
          messagesRef.current = [];
        }
      }

      // сохранение id нового запущенного интерактива.
      if (iactiveId && iactiveId !== prevIdBroadcast.current) {
        prevIdBroadcast.current = iactiveId;
      }

      // Определяем видимость титра

      if (!visibility && visibility !== isVisible) {
        setIsVisible(false);
        return;
      } else if (visibility && visibility !== isVisible) {
        setIsVisible(true);
      }

      // Определяем состояние (S1, S2 ...)
      if (
        data?.statusList[0]?.state &&
        getActiveState(data?.statusList[0]?.state) !== currentState
      ) {
        setCurrentState(getActiveState(data?.statusList[0].state));
      }

      // если список сообщений пустой
      if (
        !messagesList ||
        (Array.isArray(messagesList) && messagesList.length === 0)
      ) {
        // если имеются в списке сообщений удаленные
        if (
          messagesRef.current.some((el) =>
            deletedMessagesIdList.includes(+el.id),
          )
        ) {
          const newState = messagesRef.current.filter(
            (el) => !deletedMessagesIdList.includes(+el.id),
          );

          messagesRef.current = newState;
        }

        // если удалено текущее сообщение, отображаемое в титре
        if (deletedMessagesIdList.includes(Number(currentMessage?.id))) {
          setCurrentMessage(messagesRef.current[0]);
        }

        //запоминаем id последнего сообщения, для дальнейших запросов и обнуляем при переключении интерактивов

        if (iactiveIdRef.current && iactiveIdRef.current !== iactiveId) {
          lastMessageIdRef.current = 0;
          iactiveIdRef.current = iactiveId;
          return;
        } else {
          iactiveIdRef.current = iactiveId;
          lastMessageIdRef.current =
            messagesList[0]?.id || lastMessageIdRef.current;
        }
        return;
      }

      messagesList = messagesList.map((m) => ({
        ...m,
        message: {
          ...m.message,
          attachments: parseJSON(m.message?.attachments),
        },
      }));

      // только сообщения с image вложением
      messagesList = messagesList.filter(
        (el) =>
          Array.isArray(el.message.attachments) &&
          el.message.attachments.length > 0 &&
          typeof el.message.attachments[0] === 'object' &&
          el.message.attachments[0].type === 'image',
      );

      // только уникальные сообщения
      messagesList = messagesList.filter(
        (m) => !messagesRef.current.find((el) => el.id === m.id),
      );

      //запоминаем id последнего сообщения, для дальнейших запросов и обнуляем при переключении интерактивов
      if (iactiveIdRef.current && iactiveIdRef.current !== iactiveId) {
        lastMessageIdRef.current = 0;
        iactiveIdRef.current = iactiveId;
        return;
      } else {
        iactiveIdRef.current = iactiveId;
        lastMessageIdRef.current =
          messagesList[0]?.id || lastMessageIdRef.current;
      }

      // предзагрузка вложений
      let urls = messagesList?.map((m) => m.message?.attachments?.[0]?.url);
      if (urls && urls.length !== 0) {
        loadImages(urls);
      }

      messagesRef.current = [...messagesList, ...messagesRef.current];
      if (messagesList[0]) {
        setCurrentMessage(messagesList[0]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getBroadcastInfo();
  }, []);

  // Получение информации о титре
  useInterval(getBroadcastInfo, 1000);

  return {
    currentState,
    isVisible,
    currentMessage,
  };
};
